import { useState, useEffect } from 'react';
import { Tag, TagBadge } from './TagBadge';
import { useI18n } from '../hooks/useI18n';
import { apiFetch } from '@/utils/api';
import MultipleSelector, { Option } from '@/components/ui/multiple-selector';

interface TagSelectorProps {
  entityType: string;
  entityId: string;
  selectedTags?: Tag[];
  onChange?: (tags: Tag[]) => void;
  allowCreate?: boolean;
  localOnly?: boolean; // If true, don't sync to backend (for temporary IDs)
}

interface EntityTagRead {
  id: string;
  tag_id: string;
  tag: Tag;
}

/**
 * Multi-select tag picker with create functionality
 */
// Helper function to convert Tag to Option
function tagToOption(tag: Tag): Option {
  return {
    value: tag.id,
    label: tag.name,
    color: tag.color,
    icon: tag.icon,
  };
}

// Helper function to convert Option to Tag
function optionToTag(option: Option): Tag {
  return {
    id: option.value,
    name: option.label,
    color: option.color as string | undefined,
    icon: option.icon as string | undefined,
  };
}

export function TagSelector({
  entityType,
  entityId,
  selectedTags = [],
  onChange,
  allowCreate = true,
  localOnly = false,
}: TagSelectorProps) {
  const { t } = useI18n();
  const [allTags, setAllTags] = useState<Tag[]>([]);

  useEffect(() => {
    loadTags();
  }, []);

  const loadTags = async () => {
    try {
      const response = await apiFetch('/api/tags');
      if (response.ok) {
        const data = await response.json();
        setAllTags(data);
      }
    } catch (error) {
      console.error('Failed to load tags:', error);
    }
  };

  const handleCreateTag = async (tagName: string): Promise<Tag | null> => {
    if (!tagName.trim()) return null;

    try {
      const response = await apiFetch('/api/tags', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: tagName.trim(),
          color: generateRandomColor(),
        }),
      });

      if (response.ok) {
        const newTag: Tag = await response.json();
        setAllTags([...allTags, newTag]);
        return newTag;
      }
    } catch (error) {
      console.error('Failed to create tag:', error);
    }
    return null;
  };

  const handleAssignTag = async (tag: Tag) => {
    // If localOnly mode, just update local state without backend call
    if (localOnly) {
      onChange?.([...selectedTags, tag]);
      return;
    }

    try {
      const response = await apiFetch('/api/tags/assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tag_id: tag.id,
          entity_type: entityType,
          entity_id: entityId,
        }),
      });

      if (response.ok) {
        onChange?.([...selectedTags, tag]);
      }
    } catch (error) {
      console.error('Failed to assign tag:', error);
    }
  };

  const handleRemoveTag = async (tag: Tag) => {
    // If localOnly mode, just update local state without backend call
    if (localOnly) {
      onChange?.(selectedTags.filter((t) => t.id !== tag.id));
      return;
    }

    try {
      const response = await apiFetch(`/api/tags/entities/${entityType}/${entityId}`);
      if (response.ok) {
        const assignments: EntityTagRead[] = await response.json();
        const assignment = assignments.find((a) => a.tag_id === tag.id);
        if (assignment) {
          await apiFetch(`/api/tags/assignments/${assignment.id}`, {
            method: 'DELETE',
          });
          onChange?.(selectedTags.filter((t) => t.id !== tag.id));
        }
      }
    } catch (error) {
      console.error('Failed to remove tag:', error);
    }
  };

  const handleChange = async (options: Option[]) => {
    const newTags = options.map(optionToTag);
    const added = newTags.filter((nt) => !selectedTags.some((st) => st.id === nt.id));
    const removed = selectedTags.filter((st) => !newTags.some((nt) => nt.id === st.id));

    // Handle additions
    for (const tag of added) {
      // Check if this is a newly created tag (doesn't exist in allTags)
      const existingTag = allTags.find((t) => t.id === tag.id);
      if (!existingTag) {
        // This is a new tag created via the creatable feature
        const newTag = await handleCreateTag(tag.name);
        if (newTag) {
          await handleAssignTag(newTag);
        }
      } else {
        await handleAssignTag(tag);
      }
    }

    // Handle removals
    for (const tag of removed) {
      await handleRemoveTag(tag);
    }
  };

  return (
    <MultipleSelector
      value={selectedTags.map(tagToOption)}
      onChange={handleChange}
      options={allTags.map(tagToOption)}
      placeholder={t('tags.selectTags', 'common')}
      creatable={allowCreate}
      emptyIndicator={
        <p className="text-center text-sm leading-10 text-muted-foreground">
          {t('tags.noTagsFound', 'common')}
        </p>
      }
      renderBadge={(option, onRemove) => {
        const tag = optionToTag(option);
        return (
          <TagBadge
            tag={tag}
            onRemove={onRemove}
            size="sm"
          />
        );
      }}
    />
  );
}

function generateRandomColor(): string {
  const colors = [
    '#EF4444',
    '#F59E0B',
    '#10B981',
    '#3B82F6',
    '#6366F1',
    '#8B5CF6',
    '#EC4899',
    '#14B8A6',
  ];
  return colors[Math.floor(Math.random() * colors.length)];
}
